import posthog from "posthog-js";
import { POSTHOG_API_KEY, POSTHOG_HOST, shouldEnablePosthog, isDevelopment, isStaging } from "@/config/vars";

// Only enable tracking in production environment
const enableTracking = shouldEnablePosthog;

export const initPosthog = () => {
  // Skip initialization if not in production or API key is missing
  if (!enableTracking) {
    if (isDevelopment || isStaging) {
      console.log('🚫 PostHog: Tracking disabled in development/staging environment');
    }
    return;
  }

  if (!POSTHOG_API_KEY) {
    console.error("POSTHOG_API_KEY is missing.");
    return;
  }

  posthog.init(POSTHOG_API_KEY, {
    api_host: POSTHOG_HOST || "https://us.i.posthog.com",
    person_profiles: "identified_only",
    capture_pageview: false, // We'll handle this manually in the provider
    capture_pageleave: true,
    autocapture: {
      dom_event_allowlist: ["click", "change", "submit"],
      url_allowlist: [],
      element_allowlist: [],
      css_selector_allowlist: [],
      capture_copied_text: false,
    },
    // Session Replay
    session_recording: {
      recordCrossOriginIframes: false,
    },
    // Heatmaps
    enable_heatmaps: true,
    // Error tracking
    capture_exceptions: true,
    // Surveys
    disable_surveys: false,
    loaded: (posthog) => {
      console.log('✅ PostHog: Analytics enabled for production environment');
    },
  });
};

export const trackEvent = (eventName: string, properties = {}) => {
  try {
    if (!enableTracking) return;

    posthog.capture(eventName, { ...properties, platform: "web" });
  } catch (error) {
    console.error(`Error tracking event "${eventName}":`, error);
  }
};

export const setUserId = (userId: string) => {
  try {
    if (!enableTracking || !userId) return;

    posthog.identify(userId);
  } catch (error) {
    console.error("Error setting user ID:", error);
  }
};

export const setUserProfile = (profile: Record<any, any>) => {
  try {
    if (!enableTracking || !profile?.id) return;

    posthog.identify(profile?.id, {
      distinct_id: profile?.id,
      user_id: profile?.id,
      name: profile?.full_name,
      email: profile?.email,
      avatar: profile?.photo_thumbnail_url,
      created: profile.created_at,
      ...profile,
    });
  } catch (error) {
    console.error("Error setting user profile:", error);
  }
};

export const aliasUser = (anonId: string, userId: string) => {
  try {
    if (!enableTracking || !anonId || !userId) return;

    posthog.alias(userId, anonId);
  } catch (error) {
    console.error("Error aliasing user:", error);
  }
};

export const trackFormInput = (
  {
    form,
    page,
    name,
    field,
    value,
  }: {
    form: string;
    page: string;
    name: string;
    field: string;
    value: any;
  },
  props: Record<string, any> = {},
) => {
  try {
    if (!enableTracking) return;

    posthog.capture(`${form}: ${name} Input Change`, {
      page,
      field,
      value,
      ...props,
    });
  } catch (error) {
    console.error("Error tracking form input:", error);
  }
};

export const resetPosthog = () => {
  try {
    if (!enableTracking) return;

    posthog.reset();
    console.log("🔄 PostHog user session reset");
  } catch (error) {
    console.error("Error resetting PostHog:", error);
  }
};

// Feature flags
export const getFeatureFlag = (flagKey: string): boolean | string | undefined => {
  try {
    if (!enableTracking) {
      // Return true by default if tracking is disabled (show all features in dev/staging)
      return true;
    }

    const flagValue = posthog.isFeatureEnabled(flagKey);
    return flagValue;
  } catch (error) {
    console.error(`Error getting feature flag "${flagKey}":`, error);
    return true; // Default to enabled on error
  }
};

export const isFeatureEnabled = (flagKey: string): boolean => {
  const flagValue = getFeatureFlag(flagKey);
  return flagValue === true || flagValue === 'true';
};

// Get all feature flags (simplified version - checks individual flags)
export const getAllFeatureFlags = (): Record<string, boolean> => {
  // PostHog doesn't expose a direct method to get all flags
  // This is a placeholder that returns an empty object
  // You should check individual flags using isFeatureEnabled()
  return {};
};

// Wait for feature flags to load
export const onFeatureFlags = (callback: (flags: string[]) => void): void => {
  try {
    if (!enableTracking) return;

    posthog.onFeatureFlags(callback);
  } catch (error) {
    console.error("Error setting up feature flags callback:", error);
  }
};
